import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import dashboardService from '../services/dashboardService'
import { socketService } from '../services/api'
import { useAuthStore } from './auth'

export const useDashboardStore = defineStore('dashboard', () => {
  // State
  const summary = ref({
    totalTransactions: 0,
    totalRevenue: 0,
    activeUsers: 0,
    totalLocations: 0
  })
  
  const charts = ref({
    dailyStats: [],
    paymentBreakdown: [],
    vehicleBreakdown: []
  })
  
  const recentTransactions = ref([])
  const isLoading = ref(false)
  const lastUpdated = ref(null)
  
  const adminStats = ref({
    income: { today: 0, yesterday: 0, dayBeforeYesterday: 0, weekly: 0 },
    todayCollections: { tariffs: [], payMethods: [], operators: [], gates: [], inCounts: [], outCounts: [] },
    currentIn: [],
    currentInByGate: []
  })

  // Getters
  const formattedRevenue = computed(() => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(summary.value.totalRevenue)
  })

  const chartData = computed(() => ({
    dailyStats: charts.value.dailyStats,
    paymentBreakdown: charts.value.paymentBreakdown,
    vehicleBreakdown: charts.value.vehicleBreakdown
  }))

  // Actions
  const fetchDashboardData = async (filters = {}) => {
    try {
      isLoading.value = true
      
      const [summaryData, chartsData, transactionsData] = await Promise.all([
        dashboardService.getSummary(filters),
        dashboardService.getChartsData(filters),
        dashboardService.getRecentTransactions(filters)
      ])
      
      summary.value = { ...summary.value, ...summaryData }
      charts.value = chartsData
      recentTransactions.value = transactionsData
      lastUpdated.value = new Date()
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchAdminDashboardData = async (filters = {}) => {
    try {
      isLoading.value = true
      const data = await dashboardService.getAdminDashboardStats(filters)
      if (data) {
        // Use a more robust merge to ensure fields added in initial state aren't lost if API returns incomplete object
        adminStats.value = {
          ...adminStats.value,
          ...data,
          income: { ...adminStats.value.income, ...(data.income || {}) },
          todayCollections: { ...adminStats.value.todayCollections, ...(data.todayCollections || {}) }
        }
      }
      lastUpdated.value = new Date()
    } catch (error) {
      console.error('Error fetching admin dashboard data:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const refreshData = async () => {
    await fetchDashboardData()
  }

  const updateSummary = (newSummary) => {
    summary.value = { ...summary.value, ...newSummary }
  }

  const addTransaction = (transaction) => {
    recentTransactions.value.unshift(transaction)
    if (recentTransactions.value.length > 10) {
      recentTransactions.value.pop()
    }
    
    // Update summary
    summary.value.totalTransactions++
    summary.value.totalRevenue += parseFloat(transaction.amount || 0)
  }

  const getChartConfig = (type) => {
    const baseConfig = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        }
      }
    }

    switch (type) {
      case 'line':
        return {
          ...baseConfig,
          scales: {
            x: {
              grid: {
                display: false
              }
            },
            y: {
              beginAtZero: true,
              grid: {
                color: '#f1f5f9'
              }
            }
          }
        }
      
      case 'doughnut':
        return {
          ...baseConfig,
          cutout: '60%'
        }
      
      case 'bar':
        return {
          ...baseConfig,
          scales: {
            x: {
              grid: {
                display: false
              }
            },
            y: {
              beginAtZero: true,
              grid: {
                color: '#f1f5f9'
              }
            }
          }
        }
      
      default:
        return baseConfig
    }
  }
  
  const initSocketListeners = () => {
    const authStore = useAuthStore()
    const isAdminOrOwner = ['Administrator', 'ParkOwner'].includes(authStore.userRole)

    const handleRefresh = () => {
      console.log('Socket event received: Refreshing dashboard data...')
      if (isAdminOrOwner) {
        fetchAdminDashboardData()
      } else {
        fetchDashboardData()
      }
    }

    // Listen for operational events
    socketService.on('event:created', handleRefresh)
    socketService.on('event:updated', handleRefresh)
    socketService.on('event:paid',    handleRefresh)
    socketService.on('topup:created', handleRefresh)
    socketService.on('topup:updated', handleRefresh)
    socketService.on('topup:deleted', handleRefresh)
    
    console.log('Dashboard socket listeners initialized')
  }

  return {
    // State
    summary,
    charts,
    recentTransactions,
    isLoading,
    lastUpdated,
    adminStats,
    
    // Getters
    formattedRevenue,
    chartData,
    
    // Actions
    fetchDashboardData,
    fetchAdminDashboardData,
    refreshData,
    updateSummary,
    addTransaction,
    getChartConfig,
    initSocketListeners
  }
})
